/**
 * Arch Hangar Calculations
 *
 * Generates arch cross-section profiles for rendering and collision.
 * Supports semicircular and parabolic profiles.
 */

import type { ArchProfile } from './params'

/** 2D point on the arch cross-section */
export interface ArchPoint {
  x: number // Horizontal position (feet, -span/2 to +span/2)
  y: number // Vertical position (feet, 0 = ground)
}

/**
 * Generate the arch cross-section profile as an array of 2D points.
 * Points run from left base (-span/2, 0) over the top to right base (+span/2, 0),
 * including optional knee wall segments.
 *
 * @returns Array of {x, y} points in feet, left-to-right
 */
export function generateArchProfile(
  span_ft: number,
  crown_height_ft: number,
  knee_wall_height_ft: number,
  arch_profile: ArchProfile,
  segments: number
): ArchPoint[] {
  const halfSpan = span_ft / 2
  const points: ArchPoint[] = []

  // Left base
  points.push({ x: -halfSpan, y: 0 })

  // Left knee wall top (if any)
  if (knee_wall_height_ft > 0) {
    points.push({ x: -halfSpan, y: knee_wall_height_ft })
  }

  // Arch curve points
  const archPoints = generateCurvePoints(
    span_ft,
    crown_height_ft,
    knee_wall_height_ft,
    arch_profile,
    segments
  )
  points.push(...archPoints)

  // Right knee wall top (if any)
  if (knee_wall_height_ft > 0) {
    points.push({ x: halfSpan, y: knee_wall_height_ft })
  }

  // Right base
  points.push({ x: halfSpan, y: 0 })

  return points
}

/**
 * Generate just the curved portion of the arch (between knee wall tops).
 */
function generateCurvePoints(
  span_ft: number,
  crown_height_ft: number,
  knee_wall_height_ft: number,
  arch_profile: ArchProfile,
  segments: number
): ArchPoint[] {
  const halfSpan = span_ft / 2
  const points: ArchPoint[] = []

  switch (arch_profile) {
    case 'semicircular': {
      // Semicircle: center at (0, knee_wall_height), radius = halfSpan
      // When crown_height != halfSpan, scale the Y axis (elliptical arch)
      const radius = halfSpan
      const yScale = crown_height_ft / radius

      for (let i = 0; i <= segments; i++) {
        const angle = Math.PI * (i / segments) // 0 to PI (left to right)
        const x = -radius * Math.cos(angle) // -halfSpan to +halfSpan
        const y = knee_wall_height_ft + radius * Math.sin(angle) * yScale
        points.push({ x, y })
      }
      break
    }

    case 'parabolic': {
      // Parabola: y = crown * (1 - (2x/span)^2) + knee
      for (let i = 0; i <= segments; i++) {
        const t = i / segments // 0 to 1
        const x = -halfSpan + t * span_ft // -halfSpan to +halfSpan
        const normalized = (2 * x) / span_ft // -1 to +1
        const y = knee_wall_height_ft + crown_height_ft * (1 - normalized * normalized)
        points.push({ x, y })
      }
      break
    }
  }

  return points
}

/**
 * Calculate total building height (knee wall + crown)
 */
export function calculateTotalHeight(
  crown_height_ft: number,
  knee_wall_height_ft: number
): number {
  return knee_wall_height_ft + crown_height_ft
}

/**
 * Get the arch profile height at a given x-position.
 * Used for door height validation.
 */
export function getArchHeightAtX(
  x: number,
  span_ft: number,
  crown_height_ft: number,
  knee_wall_height_ft: number,
  arch_profile: ArchProfile
): number {
  const halfSpan = span_ft / 2
  const clampedX = Math.max(-halfSpan, Math.min(halfSpan, x))

  switch (arch_profile) {
    case 'semicircular': {
      const radius = halfSpan
      const yScale = crown_height_ft / radius
      const sinVal = Math.sqrt(Math.max(0, 1 - (clampedX / radius) ** 2))
      return knee_wall_height_ft + radius * sinVal * yScale
    }
    case 'parabolic': {
      const normalized = (2 * clampedX) / span_ft
      return knee_wall_height_ft + crown_height_ft * (1 - normalized * normalized)
    }
  }
}
